import { PortalDsBadge } from 'src/components/portal-design-system';
import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import { formatDisplayDate, formatDisplayDateTime } from 'src/helpers/date.helpers';
import type { PortalUserDetail } from '../types/users.types';
import {
  formatStatusLabel,
  formatSubscriptionLabel,
  getSubscriptionBadgeVariant,
  resolvePortalUserStatus,
} from '../utils/portal-users.mappers';

const STATUS_VARIANTS: Record<PortalUserDetail['status'], PortalDsBadgeVariant> = {
  active: 'green',
  blocked: 'red',
};

function EmailVerificationBadge({ user }: { user: PortalUserDetail }) {
  if (user.email_verified === true) {
    const verifiedLabel = user.email_verified_at
      ? `Verified ${formatDisplayDateTime(user.email_verified_at)}`
      : 'Verified';

    return (
      <PortalDsBadge
        variant="green"
        pill
      >
        {verifiedLabel}
      </PortalDsBadge>
    );
  }

  return (
    <PortalDsBadge
      variant="amber"
      pill
    >
      Not verified
    </PortalDsBadge>
  );
}

function formatCreditsLabel(user: PortalUserDetail): string {
  if (user.unlimited) {
    return 'Unlimited';
  }

  if (user.credit_balance == null) {
    return '—';
  }

  const total = user.credit_balance;
  const granted = user.granted_credit_balance;
  const purchased = user.purchased_credit_balance;
  const hasSplit = granted != null || purchased != null;

  if (!hasSplit) {
    return String(total);
  }

  const grantedLabel = granted ?? 0;
  const purchasedLabel = purchased ?? 0;
  return `${total} (${grantedLabel} granted · ${purchasedLabel} purchased)`;
}

interface UserDetailOverviewTabProps {
  user: PortalUserDetail;
  onOpenTeam?: (workspaceId: string) => void;
}

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-[12px] font-medium uppercase tracking-[0.04em] text-[#9CA3AF]">
        {label}
      </dt>
      <dd className="text-[14px] text-[#0D0F1A]">
        {children}
      </dd>
    </div>
  );
}

export function UserDetailOverviewTab({ user, onOpenTeam }: UserDetailOverviewTabProps) {
  const workspaceName = user.workspace_name ?? null;
  const status = resolvePortalUserStatus(user);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[16px] font-semibold text-[#0D0F1A]">
          {user.name}
        </span>
        {user.is_guest ? (
          <PortalDsBadge
            variant="amber"
            pill
          >
            Guest
          </PortalDsBadge>
        ) : null}
        <PortalDsBadge
          variant={STATUS_VARIANTS[status]}
          pill
        >
          {formatStatusLabel(status)}
        </PortalDsBadge>
        <EmailVerificationBadge user={user} />
        <PortalDsBadge
          variant={getSubscriptionBadgeVariant(user.subscription)}
          pill
        >
          {formatSubscriptionLabel(user.subscription)}
        </PortalDsBadge>
        {user.disable_billing_conversion ? (
          <PortalDsBadge
            variant="amber"
            pill
          >
            Billing exempt
          </PortalDsBadge>
        ) : null}
      </div>

      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <DetailField label="Email">
          <a
            href={`mailto:${user.email}`}
            className="text-[#2563EB] hover:underline"
          >
            {user.email}
          </a>
        </DetailField>

        <DetailField label="User ID">
          <span className="break-all font-mono text-[12px] text-[#5E6278]">
            {user.id}
          </span>
        </DetailField>

        <DetailField label="Workspace / team">
          {user.workspace_id ? (
            onOpenTeam ? (
              <button
                type="button"
                className="text-left text-[#2563EB] hover:underline"
                onClick={() => onOpenTeam(user.workspace_id!)}
              >
                {workspaceName ?? user.workspace_id}
              </button>
            ) : (
              <span>
                {workspaceName ?? user.workspace_id}
              </span>
            )
          ) : (
            <span className="text-[#9CA3AF]">
              —
            </span>
          )}
        </DetailField>

        <DetailField label="Credits">
          <span className={user.unlimited || user.credit_balance != null ? 'font-mono text-[13px]' : 'text-[#9CA3AF]'}>
            {formatCreditsLabel(user)}
          </span>
        </DetailField>

        <DetailField label="Created">
          {user.created_at ? formatDisplayDate(user.created_at) : '—'}
        </DetailField>

        <DetailField label="Last active">
          {user.last_active_at ? (
            formatDisplayDateTime(user.last_active_at)
          ) : (
            <span className="text-[#9CA3AF]">
              —
            </span>
          )}
        </DetailField>

        <DetailField label="Pending campaigns">
          {user.pending_campaigns === 0 ? (
            <span className="text-[#9CA3AF]">
              —
            </span>
          ) : (
            <span className="font-mono text-[13px]">
              {user.pending_campaigns}
            </span>
          )}
        </DetailField>
      </dl>
    </section>
  );
}
